import React, { PropsWithChildren, useContext } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useApolloClient } from '@apollo/client';
import { LayoutContext } from '@context/LayoutContext';
import { isDocumentLink, isEmptyLink, linkResolver } from '@utils/prismic';
import HeartOutline from '@monofront/design-system/visuals/Icons/Old/HeartOutline';
import AccountOutline from '@monofront/design-system/visuals/Icons/Old/AccountOutline';
import Dropdown from '@components/Dropdown';
import { useTranslations } from 'next-intl';
import { RoutesEnum } from '@monofront/types';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import useI18n from '@hooks/i18n/useI18n';
import { useLogout } from '@hooks/useLogout';
import { FilledLinkToWebField } from '@prismicio/client';
import SideShoppingCart from '@components/Layout/Header/SideShoppingCart';
import CompleteRegistration from '@components/CompleteRegistration';
import GoToLoginPageButton from '@components/GoToLoginPageButton';
import Checked from '@monofront/design-system/visuals/Icons/Old/Checked';
import { useAuthentication } from '@context/businessContainer';
import { useLayoutRefs } from '@context/layoutRefs';

type MenuDropdownProps = {
  href: string;
  dataTestId: string;
  icon: React.ComponentType;
};

const MenuDropdownItem = ({ children }) => {
  return (
    <li className="tw-whitespace-nowrap tw-px-1 tw-py-1 tw-text-left tw-font-semibold tw-text-black-1000 tw-transition-colors focus-within:tw-bg-beige-200 focus-within:tw-text-blue-600 hover:tw-bg-beige-200 hover:tw-text-blue-600">
      {children}
    </li>
  );
};

const MenuDropdown: React.FC<PropsWithChildren<MenuDropdownProps>> = ({
  href,
  dataTestId,
  icon: Icon,
  children,
}) => {
  return (
    <Dropdown
      hideOnMobile
      position="middle"
      label={
        <Link href={href} data-testid={dataTestId}>
          <div className="tw-size-6 desktop:tw-h-8 desktop:tw-w-8">
            <Icon />
          </div>
        </Link>
      }
    >
      <ul className="tw-flex tw-w-auto tw-min-w-40 tw-flex-col tw-rounded-2xl tw-bg-white tw-px-2 tw-py-3 tw-shadow-dropdown">
        {children}
      </ul>
    </Dropdown>
  );
};

const AuthenticatedMenu: React.FC = () => {
  const router = useRouter();
  const { headerCartRef } = useLayoutRefs();
  const { isAuthenticated, isMember } = useAuthentication();
  const apolloClient = useApolloClient();
  const { accountMenuDesktop } = useContext(LayoutContext);
  const { isFrenchMarket } = useI18n();
  const { getPath } = useLocalizedRoutes();
  const tAccount = useTranslations('Account');
  const tWishlists = useTranslations('Wishlists');
  const logout = useLogout();

  return (
    <div className="tw-flex tw-items-center tw-gap-4 desktop:tw-gap-8">
      {!isMember && (
        <div className="tw-order-1 tw-hidden desktop:tw-block">
          <CompleteRegistration />
        </div>
      )}
      {!isAuthenticated && (
        <div className="tw-order-2">
          <GoToLoginPageButton />
        </div>
      )}

      {isAuthenticated && (
        <div className="tw-order-4 desktop:tw-order-3">
          <MenuDropdown
            href="/account"
            dataTestId="AuthenticatedMenu-go-to-account"
            icon={() => (
              <div className="tw-relative">
                <AccountOutline />
                <span className="tw-absolute tw-bottom-0 tw-right-0 desktop:tw-hidden">
                  <Checked />
                </span>
              </div>
            )}
          >
            {accountMenuDesktop?.map((item, index) => {
              if (isEmptyLink(item.link)) {
                return (
                  <MenuDropdownItem key={item.link.id}>
                    {item.label}
                  </MenuDropdownItem>
                );
              }

              let href = '';

              if (isDocumentLink(item.link)) {
                href = linkResolver(item.link, item.trackingid);
              } else {
                const url = new URL((item.link as FilledLinkToWebField).url);

                if (item.trackingid) {
                  url.searchParams.append('trackingId', item.trackingid);
                }
                href = url.toString();
              }

              return (
                <MenuDropdownItem
                  key={'id' in item.link ? item.link.id : index}
                >
                  <Link
                    href={href}
                    className="tw-block"
                    data-testid={`AuthenticatedMenu-go-to-${item.label}`}
                  >
                    {item.label}
                  </Link>
                </MenuDropdownItem>
              );
            })}
            <MenuDropdownItem>
              <Link
                href="/account"
                className="tw-block tw-opacity-50"
                onClick={(e) => {
                  e.preventDefault();
                  apolloClient.cache.evict({
                    id: 'ROOT_QUERY',
                    fieldName: 'getHomePage',
                  });
                  logout();
                  router.push('/');
                }}
                data-testid="AuthenticatedMenu-logout"
              >
                {tAccount('logout')}
              </Link>
            </MenuDropdownItem>
          </MenuDropdown>
        </div>
      )}

      {isAuthenticated && (
        <div className="tw-order-3 desktop:tw-order-4">
          <MenuDropdown
            href="/pages/my-lists?tab=my-purchases"
            dataTestId="AuthenticatedMenu-go-to-pages-list"
            icon={HeartOutline}
          >
            <MenuDropdownItem>
              <Link
                href="/pages/my-lists?tab=my-purchases"
                className="tw-block"
                data-testid="AuthenticatedMenu-go-to-my-purchases"
              >
                {tAccount('myUsualProducts')}
              </Link>
            </MenuDropdownItem>
            <MenuDropdownItem>
              <Link
                href="/pages/my-lists"
                className="tw-block"
                data-testid="AuthenticatedMenu-go-to-my-lists"
              >
                {tWishlists('myLists')}
              </Link>
            </MenuDropdownItem>
            {isFrenchMarket && (
              <MenuDropdownItem>
                <Link
                  href={getPath(RoutesEnum.LISTS_RECOMMENDATIONS)}
                  className="tw-block"
                  data-testid="AuthenticatedMenu-go-to-my-recommendations"
                >
                  {tAccount('myRecommendations')}
                </Link>
              </MenuDropdownItem>
            )}
          </MenuDropdown>
        </div>
      )}

      <div ref={headerCartRef} className="tw-order-5">
        <SideShoppingCart />
      </div>
    </div>
  );
};

export default AuthenticatedMenu;
